import { Link, Redirect, useLocalSearchParams, usePathname, useRouter } from "expo-router";
import {
  Bell,
  Building2,
  CalendarDays,
  CheckCheck,
  ChevronDown,
  ChevronRight,
  Files,
  FileSignature,
  FileText,
  FolderOpen,
  LayoutDashboard,
  LogOut,
  Plus,
  Settings,
  UserRound,
  Users,
} from "./central-icons";
import { WeHonorLogomark } from "./logomark";
import {
  Fragment,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  AccessibilityInfo,
  Animated,
  Image,
  findNodeHandle,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleProp,
  StyleSheet,
  Text,
  TextInput,
  useWindowDimensions,
  View,
  ViewStyle,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { useCentralCapability } from "../hooks/useCentralCapability";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { supportsNativeDriver } from "../lib/animation";
import { cleanStaffPath, isCentralHost, staffPath } from "../lib/hosting";
import {
  central,
  layout,
  palette,
  radii,
  semantic,
  spacing,
} from "../theme/tokens";
import { useAppStore } from "../providers/app-store-context";
import { useStaffAuth } from "../providers/StaffAuthProvider";
import { getRouteAnnouncement, MAIN_CONTENT_ID } from "../lib/accessibility";
import {
  getCentralBrandAssetDownloadUrl,
  listCentralBrandAssets,
} from "../lib/central-brand-assets";
import { getCentralFuneralHomeSettings } from "../lib/central-funeral-home-settings";
import { resolveCentralTenantBrandIdentity } from "../lib/central-tenant-brand";
import {
  CENTRAL_NAVIGATION,
  type CentralNavigationChild,
  getCentralNavigationContext,
  isCentralNavigationItemActive,
  isCentralNavigationRouteActive,
} from "../config/central-navigation";
import type { Notification } from "../types/domain";
import { CentralCaseIdentityBand } from "./CentralCaseIdentityBand";
import { CentralSearchCommand } from "./CentralSearchCommand";
import { MonarchBrandLockup } from "./MonarchBrandLockup";

type ShellRole = "public" | "staff" | "family";
export type CentralTenantBrand = {
  logoUrl: string | null;
  name: string;
  shortName: string;
  useBundledMonarchLogo: boolean;
};

export function AppShell({
  children,
  role = "public",
  navigation = role === "staff" ? "staff" : "none",
  backgroundColor,
  contentStyle,
  accessibilityLabel,
  mainContentId = MAIN_CONTENT_ID,
  requireStaffSession = role === "staff",
}: PropsWithChildren<{
  role?: ShellRole;
  navigation?: "none" | "staff";
  backgroundColor?: string;
  contentStyle?: object;
  accessibilityLabel?: string;
  mainContentId?: string;
  requireStaffSession?: boolean;
}>) {
  const { ready, staffSessionActive } = useAppStore();
  const pathname = usePathname();
  const routeAnnouncement = `Navigated to ${getRouteAnnouncement(pathname)}`;
  const routeFocusRef = useRef<Text>(null);

  useEffect(() => {
    let frame: number | undefined;
    if (Platform.OS === "web" && typeof document !== "undefined") {
      frame = requestAnimationFrame(() => {
        const main = document.getElementById(mainContentId);
        main?.focus();
      });
    } else {
      const node = findNodeHandle(routeFocusRef.current);
      if (node) AccessibilityInfo.setAccessibilityFocus(node);
    }

    return () => {
      if (frame !== undefined) cancelAnimationFrame(frame);
    };
  }, [mainContentId, pathname]);

  if (role === "staff" && requireStaffSession && ready && !staffSessionActive) {
    const returnTo = isCentralHost() ? staffPath(pathname) : pathname;
    return (
      <Redirect href={`/signed-out?returnTo=${encodeURIComponent(returnTo)}`} />
    );
  }

  return (
    <SafeAreaView
      style={[
        styles.safeArea,
        role === "staff" ? styles.staffSafeArea : null,
        backgroundColor ? { backgroundColor } : null,
      ]}
    >
      <View
        style={[
          styles.root,
          role === "staff" ? styles.staffRoot : null,
          backgroundColor ? { backgroundColor } : null,
        ]}
      >
        {Platform.OS === "web" ? <SkipLink targetId={mainContentId} /> : null}
        <View
          nativeID={mainContentId}
          role="main"
          accessibilityLabel={accessibilityLabel ?? `${role} content`}
          {...(Platform.OS === "web" ? ({ tabIndex: -1 } as never) : {})}
          style={[styles.appMain, contentStyle]}
        >
          <Text
            ref={routeFocusRef}
            nativeID={`${mainContentId}-route-heading`}
            accessible
            accessibilityRole="text"
            accessibilityLabel={routeAnnouncement}
            {...(Platform.OS === "web" ? ({ tabIndex: -1 } as never) : {})}
            style={styles.visuallyHidden}
          >
            {routeAnnouncement}
          </Text>
          {children}
        </View>
        {navigation === "staff" ? <StaffTabBar /> : null}
      </View>
    </SafeAreaView>
  );
}

export function SkipLink({
  targetId = MAIN_CONTENT_ID,
}: {
  targetId?: string;
}) {
  const [focused, setFocused] = useState(false);

  if (Platform.OS !== "web") return null;

  return (
    <Pressable
      accessibilityRole="link"
      accessibilityLabel="Skip to main content"
      accessibilityHint="Moves focus past navigation to the page content"
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onPress={() => {
        if (typeof document !== "undefined") {
          const target = document.getElementById(targetId);
          target?.focus();
          target?.scrollIntoView({ block: "start" });
        }
      }}
      {...(Platform.OS === "web" ? ({ tabIndex: 0 } as never) : {})}
      style={[styles.skipLink, focused ? styles.skipLinkFocused : null]}
    >
      <Text style={styles.skipLinkText}>Skip to main content</Text>
    </Pressable>
  );
}

export function PageShell({
  children,
  role = "public",
  edgeToEdge = false,
  showBrandBar = true,
  showDecorativeBackdrop = true,
  backgroundColor,
  contentStyle,
}: PropsWithChildren<{
  edgeToEdge?: boolean;
  role?: ShellRole;
  showBrandBar?: boolean;
  showDecorativeBackdrop?: boolean;
  backgroundColor?: string;
  contentStyle?: StyleProp<ViewStyle>;
}>) {
  const centralHost = isCentralHost();
  const centralStaff = centralHost && role === "staff";
  if (centralStaff) {
    return <CentralStaffShell>{children}</CentralStaffShell>;
  }
  return (
    <AppShell
      role={role}
      navigation={role === "staff" ? "staff" : "none"}
      backgroundColor={backgroundColor ?? (centralStaff ? central.surface.canvas : undefined)}
      accessibilityLabel={
        role === "staff"
          ? "Staff workspace"
          : role === "family"
            ? "Family workspace"
            : centralHost
              ? "Central sign in"
              : "WeHonor memorial page"
      }
    >
      {role === "staff" || !showDecorativeBackdrop ? null : <AmbientBackdrop />}
      {role === "staff" || !showDecorativeBackdrop ? null : <GrainOverlay />}
      <ScrollView
        contentContainerStyle={[
          styles.scroll,
          edgeToEdge ? styles.scrollEdgeToEdge : null,
          role === "staff" ? styles.staffScroll : null,
          role === "staff" ? styles.scrollWithTabs : null,
          centralHost ? styles.centralStaff : null,
          contentStyle,
        ]}
      >
        <View
          style={[
            styles.container,
            centralStaff ? styles.centralStaffContainer : null,
          ]}
        >
          {showBrandBar ? <BrandBar centralHost={centralHost} role={role} /> : null}
          {children}
        </View>
      </ScrollView>
    </AppShell>
  );
}

const centralPrimaryIcons = {
  today: LayoutDashboard,
  cases: Files,
  documents: FolderOpen,
  forms: FileText,
  contracts: FileSignature,
  memorials: Users,
  events: CalendarDays,
  settings: Settings,
  account: UserRound,
} as const;


export function CentralStaffShell({
  children,
  contentContainerStyle,
  showCaseIdentity = true,
}: PropsWithChildren<{ contentContainerStyle?: StyleProp<ViewStyle>; showCaseIdentity?: boolean }>) {
  const { height, width } = useWindowDimensions();
  const [workspaceViewportHeight, setWorkspaceViewportHeight] = useState(height);
  const pathname = usePathname();
  // At exactly 1024px the full rail plus search and account controls exceed
  // the usable width. Switch only once there is room beyond the tablet size.
  const desktop = width > 1024;
  const brand = useCentralTenantBrand();

  return (
    <AppShell
      accessibilityLabel={`${brand.name} Central workspace`}
      backgroundColor={central.surface.canvas}
      navigation={desktop ? "none" : "staff"}
      role="staff"
    >
      <View style={styles.centralShell}>
        {desktop ? <CentralSidebar brand={brand} /> : null}
        <View style={styles.centralMainArea}>
          <CentralTopBar brand={brand} compact={!desktop} />
          {showCaseIdentity ? <CentralCaseIdentityBand /> : null}
          <ScrollView
            contentInsetAdjustmentBehavior="automatic"
            contentContainerStyle={[
              styles.centralWorkspaceScroll,
              desktop ? styles.centralWorkspaceScrollDesktop : null,
            ]}
            onLayout={({ nativeEvent }) => {
              setWorkspaceViewportHeight(Math.ceil(nativeEvent.layout.height));
            }}
            style={styles.centralWorkspaceScroller}
          >
            <View
              style={styles.centralWorkspaceContainer}
            >
              <View
                style={[
                  styles.centralWorkspaceViewport,
                  { minHeight: workspaceViewportHeight },
                  contentContainerStyle,
                ]}
              >
                {desktop ? null : (
                  <CentralContextNavigation pathname={pathname} />
                )}
                {children}
              </View>
              <CentralWorkspaceEndorsement />
            </View>
          </ScrollView>

        </View>
      </View>
    </AppShell>
  );
}

function CentralWorkspaceEndorsement() {
  return (
    <View style={styles.centralWorkspaceEndorsement}>
      <WeHonorLogomark
        accentColor={central.border.strong}
        color={central.border.strong}
        size={13}
      />
      <Text style={styles.centralWorkspaceEndorsementText}>wehonor</Text>
    </View>
  );
}

function CentralSidebar({ brand }: { brand: CentralTenantBrand }) {
  const pathname = usePathname();
  const router = useRouter();

  return (
    <View style={styles.centralSidebar}>
      <Pressable
        accessibilityLabel="Open Events"
        accessibilityRole="button"
        onPress={() => router.push("/" as never)}
        style={styles.centralSidebarBrand}
      >
        <CentralTenantLogo brand={brand} />
      </Pressable>
      <ScrollView
        contentContainerStyle={styles.centralSidebarNavContent}
        showsVerticalScrollIndicator={false}
        style={styles.centralSidebarNav}
      >
        {CENTRAL_NAVIGATION.desktop.map((item) => {
          const Icon =
            centralPrimaryIcons[
              item.icon as keyof typeof centralPrimaryIcons
            ] ?? LayoutDashboard;
          const active = isCentralNavigationItemActive(pathname, item);
          const expanded = Boolean(active && item.children?.length);
          return (
            <View key={item.title} style={styles.centralSidebarGroup}>
              <Pressable
                accessibilityLabel={item.title}
                accessibilityRole="button"
                accessibilityState={{
                  selected: active,
                  expanded: item.children?.length ? expanded : undefined,
                }}
                onPress={() => router.push(item.href as never)}
                style={[
                  styles.centralSidebarItem,
                  active ? styles.centralSidebarItemActive : null,
                ]}
              >
                <Icon
                  color={
                    active ? central.text.inverse : central.text.inverseMuted
                  }
                  size={18}
                  strokeWidth={1.9}
                />
                <Text
                  style={[
                    styles.centralSidebarItemText,
                    active ? styles.centralSidebarItemTextActive : null,
                  ]}
                >
                  {item.title}
                </Text>
                {item.children?.length ? (
                  <ChevronDown
                    color={
                      active ? central.text.inverse : central.text.inverseMuted
                    }
                    size={15}
                    strokeWidth={1.9}
                    style={
                      expanded
                        ? undefined
                        : styles.centralSidebarChevronCollapsed
                    }
                  />
                ) : null}
              </Pressable>
              {expanded ? (
                <View
                  accessibilityLabel={`${item.title} sections`}
                  style={styles.centralSidebarSubnav}
                >
                  {item.children?.map((child) => {
                    const childActive = isCentralNavigationRouteActive(
                      pathname,
                      child.href,
                    );
                    return (
                      <Pressable
                        accessibilityLabel={child.title}
                        accessibilityRole="button"
                        accessibilityState={{ selected: childActive }}
                        key={child.title}
                        onPress={() => router.push(child.href as never)}
                        style={[
                          styles.centralSidebarSubitem,
                          childActive
                            ? styles.centralSidebarSubitemActive
                            : null,
                        ]}
                      >
                        <Text
                          style={[
                            styles.centralSidebarSubitemText,
                            childActive
                              ? styles.centralSidebarSubitemTextActive
                              : null,
                          ]}
                        >
                          {child.title}
                        </Text>
                      </Pressable>
                    );
                  })}
                </View>
              ) : null}
            </View>
          );
        })}
      </ScrollView>
      <View
        accessible
        accessibilityLabel="central by wehonor"
        style={styles.centralSidebarFooter}
      >
        <Text style={styles.centralSidebarEndorsement}>central by</Text>
        <View style={styles.centralSidebarWeHonorBrand}>
          <WeHonorLogomark
            accentColor={central.text.inverseMuted}
            color={central.text.inverseMuted}
            size={18}
          />
          <Text style={styles.centralSidebarEndorsement}>wehonor</Text>
        </View>
      </View>
    </View>
  );
}

function CentralContextNavigation({ pathname }: { pathname: string }) {
  const router = useRouter();
  const params = useLocalSearchParams<{ caseId?: string | string[] }>();
  const context = getCentralNavigationContext(pathname);
  const scrollRef = useRef<ScrollView>(null);
  const childOffsets = useRef<Record<string, number>>({});
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    const activeChild = context?.children?.find((child) =>
      isCentralNavigationRouteActive(pathname, child.href),
    );
    const offset = activeChild
      ? childOffsets.current[activeChild.href]
      : undefined;
    if (offset === undefined) return;
    scrollRef.current?.scrollTo({
      animated: false,
      x: Math.max(0, offset - spacing.md),
    });
  }, [context, pathname]);

  const caseId = Array.isArray(params.caseId) ? params.caseId[0] : params.caseId;
  if (caseId || !context?.children?.length) return null;
  const hasOverflowMenu = context.children.length > 2;

  return (
    <>
      <View style={styles.centralContextNav}>
        <View
          accessibilityLabel={`${context.title} sections`}
          accessibilityRole="tablist"
          style={styles.centralContextTablist}
        >
          <ScrollView
            contentContainerStyle={styles.centralContextNavContent}
            horizontal
            ref={scrollRef}
            showsHorizontalScrollIndicator={true}
          >
            {context.children.map((child) => {
              const href = staffPath(child.href);
              const active = isCentralNavigationRouteActive(pathname, href);
              return (
                <Pressable
                  accessibilityLabel={child.title}
                  accessibilityRole="tab"
                  accessibilityState={{ selected: active }}
                  key={child.title}
                  onLayout={(event) => {
                    const offset = event.nativeEvent.layout.x;
                    childOffsets.current[child.href] = offset;
                    if (active) {
                      scrollRef.current?.scrollTo({
                        animated: false,
                        x: Math.max(0, offset - spacing.md),
                      });
                    }
                  }}
                  onPress={() => router.push(href as never)}
                  style={[
                    styles.centralContextTab,
                    active ? styles.centralContextTabActive : null,
                  ]}
                >
                  <Text
                    style={[
                      styles.centralContextTabText,
                      active ? styles.centralContextTabTextActive : null,
                    ]}
                  >
                    {child.shortTitle ?? child.title}
                  </Text>
                </Pressable>
              );
            })}
          </ScrollView>
        </View>
        {hasOverflowMenu ? (
          <Pressable
            accessibilityLabel={`More ${context.title} sections`}
            accessibilityRole="button"
            accessibilityState={{ expanded: moreOpen }}
            nativeID="central-context-more-trigger"
            onPress={() => setMoreOpen(true)}
            style={styles.centralContextMoreButton}
          >
            <Text style={styles.centralContextMoreText}>More</Text>
            <ChevronDown
              color={central.action.primary}
              size={15}
              strokeWidth={2}
            />
          </Pressable>
        ) : null}
      </View>
      {moreOpen ? (
        <CentralContextNavigationMenu
          children={context.children}
          pathname={pathname}
          title={context.title}
          onDismiss={() => setMoreOpen(false)}
        />
      ) : null}
    </>
  );
}

function CentralContextNavigationMenu({
  children,
  onDismiss,
  pathname,
  title,
}: {
  children: readonly CentralNavigationChild[];
  onDismiss: () => void;
  pathname: string;
  title: string;
}) {
  const router = useRouter();
  useCentralMenuFocus(
    "central-context-more-menu",
    "central-context-more-trigger",
    onDismiss,
  );

  return (
    <Modal
      transparent
      visible
      animationType="none"
      onRequestClose={onDismiss}
      statusBarTranslucent
    >
      <View accessibilityViewIsModal style={styles.centralMenuModal}>
        <Pressable
          accessible={false}
          focusable={false}
          onPress={onDismiss}
          {...(Platform.OS === "web" ? ({ tabIndex: -1 } as never) : {})}
          style={StyleSheet.absoluteFill}
        />
        <View
          accessibilityLabel={`${title} sections`}
          accessibilityRole="menu"
          nativeID="central-context-more-menu"
          {...(Platform.OS === "web" ? ({ tabIndex: -1 } as never) : {})}
          style={styles.centralContextMoreMenu}
        >
          <Text accessibilityRole="header" style={styles.centralMenuTitle}>
            {title}
          </Text>
          <ScrollView showsVerticalScrollIndicator>
            {children.map((child) => {
              const href = staffPath(child.href);
              const active = isCentralNavigationRouteActive(pathname, href);
              return (
                <Pressable
                  accessibilityRole="menuitem"
                  accessibilityState={{ selected: active }}
                  key={child.title}
                  onPress={() => {
                    onDismiss();
                    router.push(href as never);
                  }}
                  style={({ pressed }) => [
                    styles.centralContextMoreMenuItem,
                    active ? styles.centralContextMoreMenuItemActive : null,
                    pressed ? styles.centralMenuPressed : null,
                  ]}
                >
                  <Text
                    style={[
                      styles.centralContextMoreMenuText,
                      active ? styles.centralContextMoreMenuTextActive : null,
                    ]}
                  >
                    {child.title}
                  </Text>
                </Pressable>
              );
            })}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

function CentralTopBar({
  brand,
  compact,
}: {
  brand: CentralTenantBrand;
  compact: boolean;
}) {
  const router = useRouter();
  const { width } = useWindowDimensions();
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const {
    data,
    logout,
    markAllNotificationsRead,
    markNotificationRead,
    unreadNotificationCount,
  } = useAppStore();
  const { identity } = useStaffAuth();
  const name = identity?.displayName || identity?.email || "Staff";
  const initials =
    name
      .split(/\s+/)
      .filter(Boolean)
      .map((part) => part.charAt(0))
      .slice(0, 2)
      .join("")
      .toUpperCase() || "ST";
  // Read-only location scope (WP-1.4). The backend enforces scope from auth
  // claims; Central only displays it here. No switching affordance.
  const locationScope = identity?.access.locationRestricted
    ? identity.access.locationId ?? "Assigned location"
    : "All locations";
  const showCompactName = width >= 520;
  const showCompactProfileChevron = width >= 360;
  return (
    <View
      style={[
        styles.centralTopBar,
        compact ? styles.centralTopBarCompact : null,
      ]}
    >
      <View accessible accessibilityLabel={`${brand.name} Central`} style={styles.centralOrgButton}>
        <CentralTenantLogo brand={brand} compact />
      </View>
      <View style={styles.centralTopActions}>
        <Pressable
          accessibilityLabel={`${accountMenuOpen ? "Close" : "Open"} account menu for ${name}`}
          accessibilityRole="button"
          accessibilityState={{ expanded: accountMenuOpen }}
          nativeID="central-account-trigger"
          onPress={() => {
            setNotificationsOpen(false);
            setAccountMenuOpen((open) => !open);
          }}
          style={styles.centralProfileButton}
        >
          <Text style={styles.centralAvatar}>{initials}</Text>
          {!compact || showCompactName ? (
            <View style={styles.centralProfileCopy}>
              <Text
                numberOfLines={1}
                ellipsizeMode="tail"
                style={styles.centralProfileName}
              >
                {name}
              </Text>
              {compact ? null : (
                <Text
                  numberOfLines={1}
                  ellipsizeMode="tail"
                  style={styles.centralProfileRole}
                >
                  {identity ? identity.access.role : "staff"}
                </Text>
              )}
              {identity ? (
                <View
                  accessibilityLabel={`Location access: ${locationScope}`}
                  style={styles.centralLocationScopeChip}
                >
                  <Text
                    ellipsizeMode="tail"
                    numberOfLines={1}
                    style={styles.centralLocationScopeChipText}
                  >
                    {locationScope}
                  </Text>
                </View>
              ) : null}
            </View>
          ) : null}
          {!compact || showCompactProfileChevron ? (
            <ChevronDown
              color={central.text.inverseMuted}
              size={15}
              strokeWidth={2}
            />
          ) : null}
        </Pressable>
      </View>
      {notificationsOpen ? (
        <CentralNotificationsMenu
          compact={width < 560}
          notifications={data.notifications}
          onDismiss={() => setNotificationsOpen(false)}
          onMarkAllRead={markAllNotificationsRead}
          onOpenNotification={(notificationId) => {
            markNotificationRead(notificationId);
            setNotificationsOpen(false);
            router.push("/notifications" as never);
          }}
          onViewAll={() => {
            setNotificationsOpen(false);
            router.push("/notifications" as never);
          }}
          unreadCount={unreadNotificationCount}
        />
      ) : null}
      {accountMenuOpen ? (
        <CentralAccountMenu
          compact={width < 560}
          onDismiss={() => setAccountMenuOpen(false)}
          onNavigate={(href) => {
            setAccountMenuOpen(false);
            router.push(href as never);
          }}
          onSignOut={() => {
            setAccountMenuOpen(false);
            void logout().then(() => router.replace("/signed-out" as never));
          }}
        />
      ) : null}
    </View>
  );
}

export function getCentralTenantLogoSource(brand: CentralTenantBrand) {
  return brand.useBundledMonarchLogo
    ? null
    : brand.logoUrl
    ? { uri: brand.logoUrl }
    : null;
}

function CentralTenantLogo({
  brand,
  compact = false,
}: {
  brand: CentralTenantBrand;
  compact?: boolean;
}) {
  const source = getCentralTenantLogoSource(brand);
  if (brand.useBundledMonarchLogo) {
    return <MonarchBrandLockup size={compact ? "mobile" : "sidebar"} />;
  }
  if (source) {
    return (
      <Image
        accessibilityLabel={`${brand.name} logo`}
        resizeMode="contain"
        source={source}
        style={compact ? styles.centralMobileLogo : styles.centralSidebarLogo}
      />
    );
  }
  return (
    <View
      accessibilityLabel={`${brand.name} logo not configured`}
      style={
        compact
          ? styles.centralMobileLogoFallback
          : styles.centralSidebarLogoFallback
      }
    >
      <Building2
        color={central.text.inverseMuted}
        size={compact ? 18 : 22}
        strokeWidth={1.8}
      />
    </View>
  );
}

export function useCentralTenantBrand(): CentralTenantBrand {
  const { identity } = useStaffAuth();
  const [state, setState] = useState<{
    configuredName: string | null;
    logoUrl: string | null;
    settingsResolved: boolean;
  }>({
    configuredName: null,
    logoUrl: null,
    settingsResolved: false,
  });

  useEffect(() => {
    let active = true;
    setState({ configuredName: null, logoUrl: null, settingsResolved: false });
    if (!identity)
      return () => {
        active = false;
      };
    if (identity.access.role === "support") {
      setState({
        configuredName: null,
        logoUrl: null,
        settingsResolved: true,
      });
      return () => {
        active = false;
      };
    }
    void (async () => {
      const [settingsResult, assetsResult] = await Promise.allSettled([
        getCentralFuneralHomeSettings(),
        listCentralBrandAssets(),
      ]);
      if (!active) return;
      let nextLogoUrl: string | null = null;
      if (assetsResult.status === "fulfilled") {
        const assignedId = assetsResult.value.assignments.central_sidebar;
        const asset = assetsResult.value.assets.find(
          (candidate) =>
            candidate.id === assignedId &&
            candidate.status === "active" &&
            candidate.storagePath,
        );
        if (asset?.storagePath) {
          try {
            nextLogoUrl = await getCentralBrandAssetDownloadUrl(
              asset.storagePath,
            );
          } catch {
            nextLogoUrl = null;
          }
        }
      }
      if (active)
        setState({
          configuredName:
            settingsResult.status === "fulfilled"
              ? settingsResult.value.name.trim() || null
              : null,
          logoUrl: nextLogoUrl,
          settingsResolved: true,
        });
    })();
    return () => {
      active = false;
    };
  }, [identity?.access.funeralHomeId, identity?.access.role, identity?.uid]);

  const resolved = resolveCentralTenantBrandIdentity(
    state.configuredName,
    state.settingsResolved,
  );
  return {
    logoUrl: state.logoUrl,
    ...resolved,
  };
}

function CentralNotificationsMenu({
  compact,
  notifications,
  onDismiss,
  onMarkAllRead,
  onOpenNotification,
  onViewAll,
  unreadCount,
}: {
  compact: boolean;
  notifications: Notification[];
  onDismiss: () => void;
  onMarkAllRead: () => void;
  onOpenNotification: (notificationId: string) => void;
  onViewAll: () => void;
  unreadCount: number;
}) {
  useCentralMenuFocus(
    "central-notifications-menu",
    "central-notifications-trigger",
    onDismiss,
  );
  const recent = [...notifications]
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )
    .slice(0, 4);

  return (
    <Modal
      transparent
      visible
      animationType="fade"
      onRequestClose={onDismiss}
      statusBarTranslucent
    >
      <View accessibilityViewIsModal style={styles.centralMenuModal}>
        <Pressable
          accessible={false}
          focusable={false}
          onPress={onDismiss}
          {...(Platform.OS === "web" ? ({ tabIndex: -1 } as never) : {})}
          style={StyleSheet.absoluteFill}
        />
        <View
          accessibilityLabel="Notifications"
          accessibilityRole="menu"
          nativeID="central-notifications-menu"
          {...(Platform.OS === "web" ? ({ tabIndex: -1 } as never) : {})}
          style={[
            styles.centralNotificationsMenu,
            compact
              ? styles.centralNotificationsMenuCompact
              : styles.centralNotificationsMenuDesktop,
          ]}
        >
          <View style={styles.centralMenuHeader}>
            <View style={styles.centralMenuHeadingRow}>
              <Bell color={central.action.primary} size={18} strokeWidth={2} />
              <Text accessibilityRole="header" style={styles.centralMenuTitle}>
                Notifications
              </Text>
            </View>
            <Pressable
              accessibilityLabel="Mark all notifications as read"
              accessibilityRole="button"
              accessibilityState={{ disabled: unreadCount === 0 }}
              disabled={unreadCount === 0}
              onPress={onMarkAllRead}
              style={({ pressed }) => [
                styles.centralMenuTextButton,
                pressed ? styles.centralMenuPressed : null,
              ]}
            >
              <CheckCheck
                color={
                  unreadCount ? central.action.primary : central.text.muted
                }
                size={15}
                strokeWidth={2}
              />
              <Text
                style={[
                  styles.centralMenuTextButtonLabel,
                  unreadCount ? null : styles.centralMenuTextDisabled,
                ]}
              >
                Mark all read
              </Text>
            </Pressable>
          </View>
          <ScrollView
            showsVerticalScrollIndicator
            style={styles.centralNotificationList}
          >
            {recent.length ? (
              recent.map((notification) => (
                <Pressable
                  accessibilityLabel={`${notification.read ? "Read" : "Unread"}: ${notification.title}. ${notification.body}`}
                  accessibilityRole="button"
                  key={notification.id}
                  onPress={() => onOpenNotification(notification.id)}
                  style={({ pressed }) => [
                    styles.centralNotificationItem,
                    notification.read
                      ? null
                      : styles.centralNotificationItemUnread,
                    pressed ? styles.centralMenuPressed : null,
                  ]}
                >
                  <View
                    style={[
                      styles.centralNotificationIndicator,
                      notification.read
                        ? styles.centralNotificationIndicatorRead
                        : null,
                    ]}
                  />
                  <View style={styles.centralNotificationCopy}>
                    <Text
                      numberOfLines={1}
                      style={styles.centralNotificationTitle}
                    >
                      {notification.title}
                    </Text>
                    <Text
                      numberOfLines={2}
                      style={styles.centralNotificationBody}
                    >
                      {notification.body}
                    </Text>
                    <Text style={styles.centralNotificationTime}>
                      {relativeCentralTime(notification.createdAt)}
                    </Text>
                  </View>
                </Pressable>
              ))
            ) : (
              <View style={styles.centralNotificationEmpty}>
                <Text style={styles.centralNotificationEmptyTitle}>
                  You’re all caught up
                </Text>
                <Text style={styles.centralNotificationBody}>
                  New operational alerts will appear here.
                </Text>
              </View>
            )}
          </ScrollView>
          <Pressable
            accessibilityLabel="View all notifications"
            accessibilityRole="button"
            onPress={onViewAll}
            style={({ pressed }) => [
              styles.centralMenuFooter,
              pressed ? styles.centralMenuPressed : null,
            ]}
          >
            <Text style={styles.centralMenuFooterLabel}>
              View all notifications
            </Text>
            <ChevronRight
              color={central.action.primary}
              size={16}
              strokeWidth={2}
            />
          </Pressable>
        </View>
      </View>
    </Modal>
  );
}

function CentralAccountMenu({
  compact,
  onDismiss,
  onNavigate,
  onSignOut,
}: {
  compact: boolean;
  onDismiss: () => void;
  onNavigate: (href: string) => void;
  onSignOut: () => void;
}) {
  useCentralMenuFocus(
    "central-account-menu",
    "central-account-trigger",
    onDismiss,
  );
  return (
    <Modal
      transparent
      visible
      animationType="fade"
      onRequestClose={onDismiss}
      statusBarTranslucent
    >
      <View accessibilityViewIsModal style={styles.centralMenuModal}>
        <Pressable
          accessible={false}
          focusable={false}
          onPress={onDismiss}
          {...(Platform.OS === "web" ? ({ tabIndex: -1 } as never) : {})}
          style={StyleSheet.absoluteFill}
        />
        <View
          accessibilityLabel="Account menu"
          accessibilityRole="menu"
          nativeID="central-account-menu"
          {...(Platform.OS === "web" ? ({ tabIndex: -1 } as never) : {})}
          style={[
            styles.centralAccountMenu,
            compact
              ? styles.centralAccountMenuCompact
              : styles.centralAccountMenuDesktop,
          ]}
        >
          <Pressable
            accessibilityRole="menuitem"
            onPress={onSignOut}
            style={({ pressed }) => [
              styles.centralAccountMenuItem,
              styles.centralAccountMenuSignOut,
              pressed ? styles.centralMenuPressed : null,
            ]}
          >
            <LogOut
              color={central.status.error.foreground}
              size={17}
              strokeWidth={2}
            />
            <Text
              style={[
                styles.centralAccountMenuText,
                styles.centralAccountMenuSignOutText,
              ]}
            >
              Sign out
            </Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
}

function useCentralMenuFocus(
  menuId: string,
  triggerId: string,
  onDismiss: () => void,
  active = true,
) {
  useEffect(() => {
    if (!active) return;
    if (Platform.OS !== "web" || typeof document === "undefined") return;
    const menu = document.getElementById(menuId);
    if (!menu) return;
    const focusableSelector =
      'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusable = () =>
      Array.from(menu.querySelectorAll<HTMLElement>(focusableSelector));
    const frame = requestAnimationFrame(() => (focusable()[0] ?? menu).focus());
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onDismiss();
        return;
      }
      if (event.key !== "Tab") return;
      const controls = focusable();
      if (!controls.length) {
        event.preventDefault();
        menu.focus();
        return;
      }
      const first = controls[0]!;
      const last = controls[controls.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener("keydown", handleKeyDown);
      requestAnimationFrame(() => document.getElementById(triggerId)?.focus());
    };
  }, [active, menuId, onDismiss, triggerId]);
}

function relativeCentralTime(isoDate: string) {
  const elapsed = Math.max(0, Date.now() - new Date(isoDate).getTime());
  const minutes = Math.floor(elapsed / 60_000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "Yesterday" : `${days} days ago`;
}

export function SectionIntro({
  eyebrow,
  title,
  description,
}: {
  eyebrow: string;
  title: string;
  description: string;
}) {
  return (
    <View style={styles.intro}>
      <Text style={styles.eyebrow}>{eyebrow}</Text>
      <Text accessibilityRole="header" style={styles.title}>
        {title}
      </Text>
      <Text style={styles.description}>{description}</Text>
    </View>
  );
}

export function CentralPageHeader({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  const { width } = useWindowDimensions();
  const compactHeader = width < 768;

  return (
    <View
      style={[
        styles.centralPageHeader,
        compactHeader ? styles.centralPageHeaderCompact : null,
      ]}
    >
      <View style={styles.centralPageHeaderCopy}>
        {eyebrow ? (
          <Text style={styles.centralPageEyebrow}>{eyebrow}</Text>
        ) : null}
        <Text
          accessibilityRole="header"
          style={[
            styles.centralPageTitle,
            eyebrow ? styles.centralPageHeaderTitle : null,
            eyebrow && compactHeader
              ? styles.centralPageHeaderTitleCompact
              : null,
          ]}
        >
          {title}
        </Text>
        {description ? (
          <Text style={styles.centralPageDescription}>{description}</Text>
        ) : null}
      </View>
      {action ? (
        <View style={styles.centralPageHeaderAction}>{action}</View>
      ) : null}
    </View>
  );
}

export function StaffHeader({
  title,
  subtitle,
}: {
  title: string;
  subtitle: string;
}) {
  const centralHost = isCentralHost();
  return (
    <View
      style={[
        styles.staffHeader,
        centralHost ? styles.centralStaffHeader : null,
      ]}
    >
      <Text
        accessibilityRole="header"
        style={[
          styles.staffTitle,
          centralHost ? styles.centralStaffTitle : null,
        ]}
      >
        {title}
      </Text>
      {centralHost ? null : (
        <Text style={styles.staffSubtitle}>{subtitle}</Text>
      )}
    </View>
  );
}

function BrandBar({
  centralHost,
  role,
}: {
  centralHost: boolean;
  role: "public" | "staff" | "family";
}) {
  if (role === "staff") {
    return (
      <View
        accessible
        accessibilityLabel="Staff workspace"
        style={styles.staffBrandBar}
      >
        <View accessibilityLabel="Organization" style={styles.staffOrgButton}>
          <WeHonorLogomark size={28} />
          <Text style={styles.staffOrgName}>Staff workspace</Text>
        </View>
        <Link href={staffPath("/account") as never} asChild>
          <Pressable
            accessibilityRole="link"
            accessibilityLabel="Open account settings"
            accessibilityHint="Opens your organization and account settings"
            style={styles.staffAvatarButton}
          >
            <Text style={styles.staffAvatarText}>KS</Text>
          </Pressable>
        </Link>
      </View>
    );
  }

  if (centralHost) {
    return <CentralPublicBrandBar />;
  }

  return (
    <View
      accessible
      accessibilityLabel={
        role === "family" ? "WeHonor family workspace" : "WeHonor memorial"
      }
      style={styles.brandBar}
    >
      <WeHonorLogomark size={28} />
    </View>
  );
}

function CentralPublicBrandBar() {
  const brand = useCentralTenantBrand();
  return (
    <View
      accessible
      accessibilityLabel={`${brand.name} Central`}
      style={styles.centralSignInBrand}
    >
      <Text style={styles.centralSignInWordmark}>{brand.shortName}</Text>
      <Text style={styles.centralSignInTagline}>Central by wehonor</Text>
    </View>
  );
}

const staffTabs = CENTRAL_NAVIGATION.mobile.map((item) => ({
  navigationItem: item,
  href: item.href,
  label: item.title,
  icon:
    centralPrimaryIcons[item.icon as keyof typeof centralPrimaryIcons] ??
    LayoutDashboard,
}));

type StaffTabDefinition = (typeof staffTabs)[number];

export function StaffTabBar() {
  const pathname = usePathname();
  const activePath = cleanStaffPath(pathname);

  return (
    <View style={styles.tabBar}>
      <View accessibilityRole="tablist" style={styles.tabRow}>
        {staffTabs.map((tab, index) => {
          const href = staffPath(tab.href);
          const active = isCentralNavigationItemActive(
            activePath,
            tab.navigationItem,
          );

          return (
            <Fragment key={tab.label}>
              <View style={styles.tabSlot}>
                <StaffTab
                  tab={{ ...tab, href }}
                  active={active}
                  notificationCount={0}
                />
              </View>
            </Fragment>
          );
        })}
      </View>
    </View>
  );
}

function StaffTab({
  tab,
  active,
  notificationCount,
}: {
  tab: StaffTabDefinition;
  active: boolean;
  notificationCount: number;
}) {
  const router = useRouter();
  const [focused, setFocused] = useState(false);
  const Icon = tab.icon;
  const countLabel =
    notificationCount > 0 ? `, ${notificationCount} unread` : "";

  return (
    <Pressable
      accessibilityRole="tab"
      accessibilityLabel={`${tab.label}${countLabel}`}
      accessibilityHint={`Open ${tab.label.toLowerCase()}`}
      accessibilityState={{ selected: active }}
      onBlur={() => setFocused(false)}
      onFocus={() => setFocused(true)}
      onPress={() => router.push(tab.href as never)}
      style={({ pressed }) => [
        styles.tabItem,
        focused ? styles.tabItemFocused : null,
        pressed ? styles.tabItemPressed : null,
      ]}
    >
      <View style={[styles.tabIconWrap, active && styles.tabIconWrapActive]}>
        <Icon
          color={active ? central.action.primary : central.text.secondary}
          size={21}
          strokeWidth={1.9}
        />
        {notificationCount > 0 ? (
          <View style={styles.tabBadge}>
            <Text style={styles.tabBadgeText}>
              {notificationCount > 9 ? "9+" : String(notificationCount)}
            </Text>
          </View>
        ) : null}
      </View>
      <Text style={[styles.tabLabel, active && styles.tabLabelActive]}>
        {tab.label}
      </Text>
    </Pressable>
  );
}

function GrainOverlay() {
  return (
    <View
      pointerEvents="none"
      style={[StyleSheet.absoluteFill, styles.grainContainer]}
    >
      <View style={styles.grain} />
    </View>
  );
}

function AmbientBackdrop() {
  const { reduceMotion } = useReducedMotion();
  const driftA = useRef(new Animated.Value(0)).current;
  const driftB = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (reduceMotion) {
      driftA.setValue(0);
      driftB.setValue(0);
      return;
    }

    const createDrift = (value: Animated.Value) => {
      const loop = Animated.loop(
        Animated.sequence([
          Animated.timing(value, {
            toValue: 1,
            duration: 12000,
            useNativeDriver: supportsNativeDriver,
          }),
          Animated.timing(value, {
            toValue: 0,
            duration: 15000,
            useNativeDriver: supportsNativeDriver,
          }),
        ]),
      );
      loop.start();
      return loop;
    };

    const firstLoop = createDrift(driftA);
    let secondLoop: Animated.CompositeAnimation | undefined;
    const secondTimer = setTimeout(() => {
      secondLoop = createDrift(driftB);
    }, 2000);

    return () => {
      firstLoop.stop();
      secondLoop?.stop();
      clearTimeout(secondTimer);
    };
  }, [driftA, driftB, reduceMotion]);

  const transformA = [
    {
      translateX: driftA.interpolate({
        inputRange: [0, 1],
        outputRange: [0, 20],
      }),
    },
    {
      translateY: driftA.interpolate({
        inputRange: [0, 1],
        outputRange: [0, -15],
      }),
    },
  ];

  const transformB = [
    {
      translateX: driftB.interpolate({
        inputRange: [0, 1],
        outputRange: [0, -25],
      }),
    },
    {
      translateY: driftB.interpolate({
        inputRange: [0, 1],
        outputRange: [0, 10],
      }),
    },
  ];

  return (
    <View
      pointerEvents="none"
      style={[StyleSheet.absoluteFill, styles.ambientContainer]}
    >
      <Animated.View style={[styles.auraTop, { transform: transformA }]} />
      <Animated.View style={[styles.auraBottom, { transform: transformB }]} />
      <View style={styles.auraEdge} />
    </View>
  );
}

const styles = StyleSheet.create({
  centralShell: {
    flex: 1,
    flexDirection: "row",
    backgroundColor: central.surface.canvas,
  },
  centralMainArea: {
    flex: 1,
    minWidth: 0,
    position: "relative",
  },
  centralSidebar: {
    width: 248,
    gap: spacing.md,
    paddingHorizontal: 14,
    paddingVertical: spacing.lg,
    backgroundColor: central.surface.sidebar,
    borderRightWidth: 0,
  },
  centralSidebarBrand: {
    alignItems: "center",
    justifyContent: "center",
    minHeight: 80,
    paddingHorizontal: 4,
  },
  centralSidebarLogo: {
    width: 176,
    height: 36,
    marginBottom: spacing.xs,
  },
  centralSidebarLogoFallback: {
    width: 40,
    height: 40,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: central.radius.control,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.24)",
    backgroundColor: "rgba(255,255,255,0.08)",
    marginBottom: spacing.xs,
  },
  centralSidebarName: {
    color: central.text.inverse,
    fontFamily: central.typography.strong,
    fontSize: 16,
    lineHeight: 20,
    maxWidth: 196,
  },
  centralSidebarProductLine: {
    alignItems: "center",
    flexDirection: "row",
    gap: spacing.xs,
  },
  centralSidebarEndorsement: {
    color: "rgba(255,255,255,0.48)",
    fontFamily: central.typography.label,
    fontSize: 9,
    letterSpacing: 0.5,
    lineHeight: 13,
  },
  centralSidebarNav: {
    flex: 1,
  },
  centralSidebarNavContent: {
    gap: 4,
  },
  centralSidebarFooter: {
    alignItems: "center",
    flexDirection: "row",
    gap: spacing.xs,
    opacity: 0.68,
    paddingHorizontal: spacing.sm,
    paddingTop: spacing.md,
  },
  centralSidebarWeHonorBrand: {
    alignItems: "center",
    flexDirection: "row",
    gap: spacing.xxs,
  },
  centralSidebarGroup: {
    gap: 2,
  },
  centralSidebarItem: {
    minHeight: 46,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    paddingHorizontal: spacing.sm,
    borderRadius: central.radius.control,
  },
  centralSidebarItemActive: {
    backgroundColor: central.surface.sidebarActive,
  },
  centralSidebarItemText: {
    flex: 1,
    color: central.text.inverseMuted,
    fontFamily: central.typography.bodyMedium,
    fontSize: 13,
    lineHeight: 18,
  },
  centralSidebarItemTextActive: {
    color: central.text.inverse,
    fontFamily: central.typography.label,
  },
  centralSidebarChevronCollapsed: {
    transform: [{ rotate: "-90deg" }],
  },
  centralSidebarSubnav: {
    gap: 2,
    marginBottom: spacing.xs,
    marginLeft: 26,
    paddingLeft: spacing.sm,
    borderLeftWidth: 1,
    borderLeftColor: "rgba(255,255,255,0.22)",
  },
  centralSidebarSubitem: {
    minHeight: 36,
    justifyContent: "center",
    paddingHorizontal: spacing.sm,
    borderRadius: central.radius.control,
  },
  centralSidebarSubitemActive: {
    backgroundColor: "rgba(255,255,255,0.10)",
  },
  centralSidebarSubitemText: {
    color: central.text.inverseMuted,
    fontFamily: central.typography.bodyMedium,
    fontSize: 12,
    lineHeight: 17,
  },
  centralSidebarSubitemTextActive: {
    color: central.text.inverse,
    fontFamily: central.typography.label,
  },
  centralContextNav: {
    minHeight: 46,
    alignItems: "stretch",
    borderBottomWidth: 1,
    borderBottomColor: central.border.subtle,
    flexDirection: "row",
  },
  centralContextTablist: {
    flex: 1,
    minWidth: 0,
  },
  centralContextNavContent: {
    alignItems: "center",
    gap: spacing.xs,
    paddingHorizontal: 0,
  },
  centralContextTab: {
    minHeight: 44,
    justifyContent: "center",
    paddingHorizontal: spacing.xs,
    borderBottomWidth: 2,
    borderBottomColor: "transparent",
  },
  centralContextTabActive: {
    borderBottomColor: central.action.primary,
  },
  centralContextTabText: {
    color: central.text.secondary,
    fontFamily: central.typography.bodyMedium,
    fontSize: 12,
    lineHeight: 17,
  },
  centralContextTabTextActive: {
    color: central.action.primary,
    fontFamily: central.typography.label,
  },
  centralContextMoreButton: {
    minHeight: 44,
    alignItems: "center",
    flexDirection: "row",
    gap: 4,
    justifyContent: "center",
    paddingHorizontal: spacing.sm,
    borderLeftColor: central.border.subtle,
    borderLeftWidth: 1,
    backgroundColor: central.surface.raised,
  },
  centralContextMoreText: {
    color: central.action.primary,
    fontFamily: central.typography.label,
    fontSize: 12,
    lineHeight: 17,
  },
  centralContextMoreMenu: {
    position: "absolute",
    top: 72,
    right: spacing.md,
    left: spacing.md,
    maxHeight: "72%",
    gap: 4,
    padding: spacing.sm,
    borderRadius: central.radius.card,
    borderWidth: 1,
    borderColor: central.border.subtle,
    backgroundColor: central.surface.raised,
    ...central.shadow,
  },
  centralContextMoreMenuItem: {
    minHeight: 44,
    justifyContent: "center",
    paddingHorizontal: spacing.sm,
    borderRadius: central.radius.control,
  },
  centralContextMoreMenuItemActive: {
    backgroundColor: central.action.selected,
  },
  centralContextMoreMenuText: {
    color: central.text.primary,
    fontFamily: central.typography.bodyMedium,
    fontSize: 13,
    lineHeight: 18,
  },
  centralContextMoreMenuTextActive: {
    color: central.action.primary,
    fontFamily: central.typography.label,
  },
  centralTopBar: {
    minHeight: 72,
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.md,
    paddingHorizontal: spacing.lg,
    backgroundColor: central.action.primaryPressed,
    borderBottomWidth: 0,
    zIndex: semantic.zIndex.sticky,
  },
  centralTopBarCompact: {
    minHeight: 64,
    justifyContent: "space-between",
    paddingHorizontal: spacing.md,
  },
  centralSearch: {
    flex: 1,
    minWidth: 0,
    flexShrink: 1,
    maxWidth: 620,
    minHeight: 44,
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: central.radius.control,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.26)",
    backgroundColor: "rgba(255,255,255,0.10)",
  },
  centralSearchInput: {
    flex: 1,
    minWidth: 0,
    flexShrink: 1,
    minHeight: 38,
    color: central.text.inverse,
    fontFamily: central.typography.bodyMedium,
    fontSize: 13,
  },
  centralOrgButton: {
    minHeight: 44,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  centralMobileLogo: {
    width: 54,
    height: 28,
  },
  centralMobileLogoFallback: {
    width: 32,
    height: 32,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: central.radius.control,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.22)",
    backgroundColor: "rgba(255,255,255,0.08)",
  },
  centralMobileBrandCopy: {
    flexShrink: 1,
    gap: 1,
  },
  centralOrgName: {
    flexShrink: 1,
    color: central.text.inverse,
    fontFamily: central.typography.label,
    fontSize: 15,
    lineHeight: 18,
    maxWidth: 120,
  },
  centralMobileEndorsement: {
    color: central.text.inverseMuted,
    fontFamily: central.typography.bodyMedium,
    fontSize: 9,
    letterSpacing: 0.25,
    lineHeight: 12,
  },
  centralTopActions: {
    flexShrink: 0,
    marginLeft: "auto",
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  centralFloatingNewCase: {
    position: "absolute",
    width: 56,
    height: 56,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 28,
    backgroundColor: central.action.primary,
    shadowColor: central.text.primary,
    shadowOpacity: 0.2,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 4 },
  },
  centralFloatingNewCaseDesktop: {
    right: spacing.lg,
    bottom: spacing.lg,
    zIndex: semantic.zIndex.sticky,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.28)",
    elevation: semantic.elevation.sticky,
  },
  centralFloatingNewCaseCompact: {
    top: -28,
    left: "50%",
    zIndex: semantic.zIndex.drawer,
    borderWidth: 4,
    borderColor: central.surface.raised,
    elevation: semantic.elevation.drawer,
    transform: [{ translateX: -28 }],
  },
  centralFloatingNewCasePressed: {
    backgroundColor: central.action.primaryPressed,
    opacity: 0.9,
  },
  centralIconButton: {
    position: "relative",
    width: 44,
    height: 44,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: central.radius.control,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.26)",
    backgroundColor: "rgba(255,255,255,0.10)",
  },
  centralUnreadDot: {
    position: "absolute",
    top: 8,
    right: 8,
    width: 7,
    height: 7,
    borderRadius: 4,
    backgroundColor: semantic.status.error.foreground,
  },
  centralProfileButton: {
    minWidth: 0,
    minHeight: 44,
    flexDirection: "row",
    alignItems: "center",
    gap: 9,
    paddingHorizontal: 8,
    borderRadius: central.radius.control,
    backgroundColor: "rgba(255,255,255,0.10)",
  },
  centralAvatar: {
    width: 28,
    height: 28,
    overflow: "hidden",
    textAlign: "center",
    textAlignVertical: "center",
    borderRadius: 14,
    color: central.text.inverse,
    backgroundColor: "rgba(255,255,255,0.18)",
    fontFamily: central.typography.strong,
    fontSize: 10,
    lineHeight: 28,
  },
  centralProfileName: {
    color: central.text.inverse,
    fontFamily: central.typography.label,
    fontSize: 12,
    lineHeight: 16,
  },
  centralProfileCopy: {
    flexShrink: 1,
    minWidth: 0,
    maxWidth: 160,
  },
  centralProfileRole: {
    color: central.text.inverseMuted,
    fontFamily: central.typography.bodyMedium,
    fontSize: 10,
    lineHeight: 13,
    textTransform: "capitalize",
  },
  centralLocationScopeChip: {
    alignSelf: "flex-start",
    backgroundColor: central.surface.sidebarActive,
    borderRadius: central.radius.badge,
    maxWidth: "100%",
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
  },
  centralLocationScopeChipText: {
    color: central.text.inverseMuted,
    fontFamily: central.typography.bodyMedium,
    fontSize: 10,
    lineHeight: 13,
  },
  centralMenuModal: {
    flex: 1,
    backgroundColor: "rgba(18,47,99,0.08)",
  },
  centralNotificationsMenu: {
    position: "absolute",
    overflow: "hidden",
    borderRadius: central.radius.card,
    borderWidth: 1,
    borderColor: central.border.subtle,
    backgroundColor: central.surface.raised,
    ...central.shadow,
  },
  centralNotificationsMenuDesktop: {
    top: 62,
    right: spacing.lg,
    width: 360,
  },
  centralNotificationsMenuCompact: {
    top: 58,
    left: spacing.sm,
    right: spacing.sm,
  },
  centralMenuHeader: {
    minHeight: 54,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: central.border.subtle,
  },
  centralMenuHeadingRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  centralMenuTitle: {
    color: central.text.primary,
    fontFamily: central.typography.label,
    fontSize: 15,
    lineHeight: 20,
  },
  centralMenuTextButton: {
    minHeight: 44,
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
    paddingHorizontal: spacing.xs,
    borderRadius: central.radius.control,
  },
  centralMenuTextButtonLabel: {
    color: central.action.primary,
    fontFamily: central.typography.bodyMedium,
    fontSize: 11,
    lineHeight: 16,
  },
  centralMenuTextDisabled: {
    color: central.text.muted,
  },
  centralNotificationList: {
    maxHeight: 360,
  },
  centralNotificationItem: {
    minHeight: 82,
    flexDirection: "row",
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: central.border.subtle,
  },
  centralNotificationItemUnread: {
    backgroundColor: central.action.selected,
  },
  centralNotificationIndicator: {
    width: 7,
    height: 7,
    marginTop: 6,
    borderRadius: 4,
    backgroundColor: central.action.primary,
  },
  centralNotificationIndicatorRead: {
    backgroundColor: central.border.strong,
  },
  centralNotificationCopy: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  centralNotificationTitle: {
    color: central.text.primary,
    fontFamily: central.typography.label,
    fontSize: 12,
    lineHeight: 17,
  },
  centralNotificationBody: {
    color: central.text.secondary,
    fontFamily: central.typography.body,
    fontSize: 11,
    lineHeight: 16,
  },
  centralNotificationTime: {
    color: central.text.muted,
    fontFamily: central.typography.bodyMedium,
    fontSize: 10,
    lineHeight: 14,
  },
  centralNotificationEmpty: {
    gap: 3,
    padding: spacing.lg,
  },
  centralNotificationEmptyTitle: {
    color: central.text.primary,
    fontFamily: central.typography.label,
    fontSize: 13,
    lineHeight: 18,
  },
  centralMenuFooter: {
    minHeight: 46,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    paddingHorizontal: spacing.md,
  },
  centralMenuFooterLabel: {
    color: central.action.primary,
    fontFamily: central.typography.label,
    fontSize: 12,
    lineHeight: 17,
  },
  centralMenuPressed: {
    opacity: 0.72,
  },
  centralAccountMenu: {
    position: "absolute",
    width: 220,
    padding: 6,
    gap: 2,
    borderRadius: central.radius.card,
    borderWidth: 1,
    borderColor: central.border.subtle,
    backgroundColor: central.surface.raised,
    ...central.shadow,
  },
  centralAccountMenuDesktop: {
    top: 62,
    right: spacing.lg,
  },
  centralAccountMenuCompact: {
    top: 58,
    right: spacing.sm,
  },
  centralAccountMenuItem: {
    minHeight: 44,
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.sm,
    paddingHorizontal: spacing.sm,
    borderRadius: central.radius.control,
  },
  centralAccountMenuSignOut: {
    borderTopColor: central.border.subtle,
    borderTopWidth: 1,
    marginTop: 4,
  },
  centralAccountMenuText: {
    color: central.text.primary,
    fontFamily: central.typography.label,
    fontSize: 13,
    lineHeight: 18,
  },
  centralAccountMenuSignOutText: {
    color: central.status.error.foreground,
  },
  centralWorkspaceScroller: {
    flex: 1,
  },
  centralWorkspaceScroll: {
    padding: spacing.lg,
    paddingBottom: 128,
  },
  centralWorkspaceScrollDesktop: {
    paddingBottom: spacing.lg,
  },
  centralWorkspaceContainer: {
    width: "100%",
    minWidth: 0,
    maxWidth: central.layout.maxWidth,
    alignSelf: "center",
    gap: spacing.md,
  },
  centralWorkspaceViewport: {
    width: "100%",
    minWidth: 0,
    gap: spacing.md,
  },
  centralWorkspaceEndorsement: {
    alignSelf: "center",
    alignItems: "center",
    flexDirection: "row",
    gap: spacing.xs,
    marginTop: spacing.xl,
    opacity: 0.5,
  },
  centralWorkspaceEndorsementText: {
    color: central.border.strong,
    fontFamily: central.typography.bodyMedium,
    fontSize: 9,
    letterSpacing: 0.25,
    lineHeight: 13,
  },
  appMain: {
    flex: 1,
    minWidth: 0,
  },
  visuallyHidden: {
    position: "absolute",
    left: -10000,
    top: 0,
    width: 1,
    height: 1,
    overflow: "hidden",
  },
  skipLink: {
    position: "absolute",
    top: -80,
    left: spacing.md,
    zIndex: semantic.zIndex.sticky,
    minHeight: 44,
    paddingHorizontal: spacing.md,
    justifyContent: "center",
    borderRadius: radii.control,
    backgroundColor: semantic.focus.highContrast,
  },
  skipLinkFocused: {
    top: spacing.xs,
  },
  skipLinkText: {
    color: semantic.highContrast.inverseText,
    fontFamily: "Manrope_700Bold",
    fontSize: 14,
  },
  safeArea: {
    flex: 1,
    backgroundColor: palette.canvas,
  },
  staffSafeArea: {
    backgroundColor: semantic.surface.staff,
  },
  root: {
    flex: 1,
    backgroundColor: palette.canvas,
  },
  staffRoot: {
    backgroundColor: semantic.surface.staff,
  },
  scroll: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.md,
    paddingBottom: spacing.xxl,
  },
  scrollEdgeToEdge: {
    paddingHorizontal: 0,
    paddingTop: 0,
  },
  staffScroll: {
    paddingHorizontal: 14,
    paddingTop: 0,
  },
  centralStaff: {
    backgroundColor: central.surface.canvas,
    paddingHorizontal: spacing.md,
    paddingTop: spacing.md,
  },
  centralStaffContainer: {
    maxWidth: 1320,
    gap: spacing.md,
  },
  scrollWithTabs: {
    paddingBottom: 132,
  },
  container: {
    width: "100%",
    maxWidth: layout.maxWidth,
    alignSelf: "center",
    gap: spacing.lg,
  },
  brandBar: {
    gap: 4,
    paddingTop: 6,
  },
  centralSignInBrand: {
    gap: 2,
    paddingTop: spacing.xs,
  },
  centralSignInWordmark: {
    color: semantic.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 22,
    letterSpacing: 2.4,
    lineHeight: 27,
  },
  centralSignInTagline: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_700Bold",
    fontSize: 10,
    letterSpacing: 0.45,
    lineHeight: 14,
  },
  staffBrandBar: {
    minHeight: 60,
    marginHorizontal: -14,
    marginBottom: 8,
    paddingHorizontal: 16,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: semantic.surface.staffRaised,
    borderBottomWidth: 1,
    borderBottomColor: semantic.border.staff,
  },
  staffOrgButton: {
    minHeight: 44,
    flexDirection: "row",
    alignItems: "center",
    gap: 9,
  },
  staffOrgName: {
    color: palette.ink,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 16,
    lineHeight: 22,
  },
  staffAvatarButton: {
    minWidth: 44,
    minHeight: 44,
    alignItems: "center",
    justifyContent: "center",
  },
  staffAvatarText: {
    width: 30,
    height: 30,
    borderRadius: 15,
    overflow: "hidden",
    textAlign: "center",
    textAlignVertical: "center",
    color: palette.white,
    backgroundColor: palette.forest,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 11,
    lineHeight: 30,
  },
  brandRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  brandWordmark: {
    color: palette.ink,
    fontFamily: "CormorantGaramond_600SemiBold",
    fontSize: 26,
    lineHeight: 28,
  },
  brandLine: {
    color: palette.cedar,
    fontFamily: "Manrope_500Medium",
    fontSize: 12,
  },
  intro: {
    gap: 8,
  },
  eyebrow: {
    color: palette.cedar,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 12,
    letterSpacing: 1.2,
    textTransform: "uppercase",
  },
  title: {
    color: palette.ink,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 54,
    lineHeight: 56,
    maxWidth: 860,
  },
  description: {
    color: palette.inkSoft,
    fontFamily: "Manrope_400Regular",
    fontSize: 16,
    lineHeight: 27,
    maxWidth: 760,
  },
  staffHeader: {
    gap: 8,
    paddingTop: 12,
  },
  centralStaffHeader: {
    gap: 0,
    paddingTop: 4,
  },
  staffTitle: {
    color: palette.ink,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 36,
    lineHeight: 42,
  },
  centralStaffTitle: {
    color: central.text.primary,
    fontFamily: central.typography.display,
    fontSize: 24,
    lineHeight: 31,
  },
  centralPageHeader: {
    alignItems: "flex-start",
    flexDirection: "row",
    flexWrap: "wrap",
    marginBottom: 0,
    gap: spacing.sm,
    justifyContent: "space-between",
    paddingTop: spacing.xs,
  },
  centralPageHeaderCompact: {
    marginBottom: 0,
  },
  centralPageHeaderCopy: {
    flex: 1,
    gap: 3,
    maxWidth: central.layout.readingWidth,
    minWidth: 260,
  },
  centralPageHeaderAction: {
    alignSelf: "flex-start",
    maxWidth: "100%",
    minWidth: 0,
  },
  centralPageEyebrow: {
    color: central.action.primary,
    fontFamily: central.typography.label,
    fontSize: 10,
    letterSpacing: 0.55,
    lineHeight: 14,
    textTransform: "uppercase",
  },
  centralPageTitle: {
    color: central.text.primary,
    fontFamily: central.typography.display,
    fontSize: 24,
    lineHeight: 31,
  },
  centralPageHeaderTitle: {
    marginTop: 0,
  },
  centralPageHeaderTitleCompact: {
    marginTop: 0,
  },
  centralPageDescription: {
    color: central.text.secondary,
    fontFamily: central.typography.body,
    fontSize: 13,
    lineHeight: 19,
    maxWidth: central.layout.readingWidth,
    marginTop: 2,
  },
  staffSubtitle: {
    color: palette.inkSoft,
    fontFamily: "Manrope_500Medium",
    fontSize: 16,
    lineHeight: 24,
    maxWidth: 720,
  },
  grainContainer: {
    opacity: 0.03,
  },
  ambientContainer: {
    overflow: "hidden",
  },
  grain: {
    // StyleSheet.absoluteFillObject was removed from the RN 0.85 types.
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundColor: palette.ink,
  },
  auraTop: {
    position: "absolute",
    top: -140,
    left: -80,
    width: 360,
    height: 360,
    borderRadius: 180,
    backgroundColor: semantic.overlay.gold,
  },
  auraBottom: {
    position: "absolute",
    bottom: 30,
    right: -60,
    width: 260,
    height: 260,
    borderRadius: 130,
    backgroundColor: semantic.overlay.forestSubtle,
  },
  auraEdge: {
    position: "absolute",
    top: 120,
    right: 80,
    width: 160,
    height: 160,
    borderRadius: 80,
    backgroundColor: semantic.overlay.cedarSubtle,
  },
  tabBar: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    minHeight: central.layout.mobileActionHeight,
    justifyContent: "center",
    overflow: "visible",
    borderTopWidth: 1,
    borderTopColor: central.border.subtle,
    backgroundColor: central.surface.raised,
    paddingHorizontal: spacing.md,
    paddingVertical: 10,
    shadowColor: central.text.primary,
    shadowOpacity: 0.05,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: -4 },
    elevation: semantic.elevation.sticky,
  },
  tabRow: {
    width: "100%",
    maxWidth: 560,
    alignSelf: "center",
    flexDirection: "row",
    gap: spacing.xs,
    position: "relative",
    zIndex: 1,
  },
  tabSlot: {
    flex: 1,
    minWidth: 0,
  },
  tabFabSpacer: {
    width: 56,
    flexShrink: 0,
  },
  tabItem: {
    width: "100%",
    alignItems: "center",
    gap: 6,
    minHeight: 58,
    paddingVertical: 6,
    borderRadius: radii.sm,
    borderWidth: 2,
    borderColor: "transparent",
  },
  tabItemFocused: {
    borderColor: central.action.primary,
  },
  tabItemPressed: {
    opacity: 0.78,
  },
  tabIconWrap: {
    width: 36,
    height: 36,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "transparent",
    backgroundColor: "transparent",
  },
  tabIconWrapActive: {
    borderColor: central.border.subtle,
    backgroundColor: central.action.selected,
  },
  tabLabel: {
    color: central.text.secondary,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 11,
  },
  tabLabelActive: {
    color: central.action.primary,
    fontFamily: "Manrope_700Bold",
  },
  tabBadge: {
    position: "absolute",
    top: 2,
    right: 2,
    minWidth: 15,
    height: 15,
    borderRadius: 8,
    backgroundColor: palette.goldSoft,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 3,
  },
  tabBadgeText: {
    color: palette.forestDark,
    fontFamily: "Manrope_700Bold",
    fontSize: 8,
    lineHeight: 10,
  },
});
